import { supabase, TABLES } from './supabase';
import type {
  UserWithPermissions,
  TablePermission,
  PermissionState,
  TableInfo,
  AdminStats,
  UserRole,
} from '@/types/admin';

// ============ User Management Functions ============

// Create a new user with Supabase Auth
export async function createUser(
  email: string,
  password: string,
  fullName: string,
  role: UserRole
): Promise<UserWithPermissions> {
  try {
    // Sign up user using the client-safe signUp method
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    if (signUpError) {
      console.error('Error signing up user:', signUpError);
      throw new Error(`Failed to create user: ${signUpError.message}`);
    }

    if (!signUpData.user) {
      throw new Error('User was not created');
    }

    // Create profile record with the specified role
    const { data: profile, error: profileError } = await supabase
      .from(TABLES.PROFILES)
      .insert({
        id: signUpData.user.id,
        email,
        full_name: fullName,
        role,
      })
      .select('*')
      .single();

    if (profileError) {
      console.error('Error creating profile:', profileError);
      // Profile creation failed, but auth user was created
      throw new Error(`User created but profile failed: ${profileError.message}`);
    }

    return {
      ...profile,
      permissions: [],
    };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
}

// Fetch all users with their permissions
export async function fetchUsersWithPermissions(): Promise<UserWithPermissions[]> {
  try {
    // First, try to fetch profiles
    const { data: profiles, error: profileError } = await supabase
      .from(TABLES.PROFILES)
      .select('*')
      .order('created_at', { ascending: false });

    if (profileError) {
      console.warn('Error fetching profiles:', profileError);
      return [];
    }

    if (!profiles || profiles.length === 0) {
      return [];
    }

    // Then, try to fetch permissions for all users
    const { data: permissions, error: permError } = await supabase
      .from('table_permissions')
      .select('*');

    // Merge profiles with their permissions
    const usersWithPermissions: UserWithPermissions[] = profiles.map((profile) => {
      const userPerms = permissions?.filter((p) => p.user_id === profile.id) || [];
      return {
        ...profile,
        permissions: permError ? [] : userPerms,
      };
    });

    return usersWithPermissions;
  } catch (error) {
    console.error('Error fetching users with permissions:', error);
    // Return empty array instead of throwing
    return [];
  }
}

// Fetch a single user with permissions
export async function fetchUserWithPermissions(
  userId: string
): Promise<UserWithPermissions | null> {
  try {
    const { data: profile, error: profileError } = await supabase
      .from(TABLES.PROFILES)
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      return null;
    }

    const { data: permissions, error: permError } = await supabase
      .from('table_permissions')
      .select('*')
      .eq('user_id', userId);

    return {
      ...profile,
      permissions: permError ? [] : permissions || [],
    };
  } catch (error) {
    console.error('Error fetching user with permissions:', error);
    return null;
  }
}

// Update user profile
export async function updateUserProfile(
  userId: string,
  updates: { fullName?: string; role?: UserRole }
): Promise<void> {
  try {
    const { error } = await supabase
      .from(TABLES.PROFILES)
      .update({
        full_name: updates.fullName,
        role: updates.role,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}

// ============ Permission Management Functions ============

// Update or create permissions for a user
export async function upsertUserPermissions(
  userId: string,
  permissions: PermissionState
): Promise<void> {
  try {
    // First, delete existing permissions
    const { error: deleteError } = await supabase
      .from('table_permissions')
      .delete()
      .eq('user_id', userId);

    if (deleteError) throw deleteError;

    // Then insert new permissions
    const permissionEntries = Object.entries(permissions)
      .filter(([, perms]) => perms.enabled)
      .map(([tableName, perms]) => ({
        user_id: userId,
        table_name: tableName,
        can_read: perms.read,
        can_write: perms.write,
        can_delete: perms.delete,
        allowed_fields: perms.fields,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

    if (permissionEntries.length > 0) {
      const { error: insertError } = await supabase
        .from('table_permissions')
        .insert(permissionEntries);

      if (insertError) throw insertError;
    }
  } catch (error) {
    console.error('Error upserting permissions:', error);
    throw error;
  }
}

// Update single table permission
export async function updateTablePermission(
  userId: string,
  tableName: string,
  permission: {
    can_read?: boolean;
    can_write?: boolean;
    can_delete?: boolean;
    allowed_fields?: string[];
  }
): Promise<void> {
  try {
    const { error } = await supabase
      .from('table_permissions')
      .upsert({
        user_id: userId,
        table_name: tableName,
        ...permission,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id, table_name',
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error updating table permission:', error);
    throw error;
  }
}

// Delete all permissions for a user
export async function deleteUserPermissions(userId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('table_permissions')
      .delete()
      .eq('user_id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting user permissions:', error);
    throw error;
  }
}

// Delete single table permission
export async function deleteTablePermission(
  userId: string,
  tableName: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('table_permissions')
      .delete()
      .eq('user_id', userId)
      .eq('table_name', tableName);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting table permission:', error);
    throw error;
  }
}

// ============ Table Information Functions ============

// Get all available tables and their columns
export async function fetchTableInfo(): Promise<TableInfo[]> {
  try {
    // Try to call the RPC function
    const { data, error } = await supabase.rpc('get_public_tables');

    if (error || !data) {
      // Fallback: return known tables based on profiles
      console.warn('Could not fetch table info via RPC, using fallback');
      return [
        { table_name: 'profiles', columns: ['id', 'email', 'full_name', 'role', 'created_at', 'updated_at'] },
      ];
    }

    return data;
  } catch (error) {
    console.error('Error fetching table info:', error);
    // Return fallback
    return [
      { table_name: 'profiles', columns: ['id', 'email', 'full_name', 'role', 'created_at', 'updated_at'] },
    ];
  }
}

// Get columns for a specific table
export async function fetchTableColumns(
  tableName: string
): Promise<string[]> {
  try {
    const { data, error } = await supabase.rpc('get_table_columns', {
      p_table_name: tableName,
    });

    if (error || !data) {
      // Fallback columns
      return ['id', 'created_at', 'updated_at'];
    }

    return (data || []).map((col: { column_name: string }) => col.column_name);
  } catch (error) {
    console.error('Error fetching table columns:', error);
    return ['id', 'created_at', 'updated_at'];
  }
}

// ============ Admin Stats Functions ============

export async function fetchAdminStats(): Promise<AdminStats> {
  try {
    const { data: users, error: usersError } = await supabase
      .from(TABLES.PROFILES)
      .select('role');

    if (usersError) throw usersError;

    const tableInfo = await fetchTableInfo();

    const stats: AdminStats = {
      totalUsers: users?.length || 0,
      adminCount: users?.filter((u) => u.role === 'admin').length || 0,
      managerCount: users?.filter((u) => u.role === 'manager').length || 0,
      userCount: users?.filter((u) => u.role === 'user').length || 0,
      totalTables: tableInfo.length,
    };

    return stats;
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    return {
      totalUsers: 0,
      adminCount: 0,
      managerCount: 0,
      userCount: 0,
      totalTables: 0,
    };
  }
}

// ============ User Deletion ============

export async function deleteUser(userId: string): Promise<void> {
  try {
    // First delete profile (which cascades to table_permissions)
    const { error } = await supabase
      .from(TABLES.PROFILES)
      .delete()
      .eq('id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
}

// ============ Helper Functions ============

// Convert API permissions to UI PermissionState
export function permissionsToState(
  permissions: TablePermission[]
): PermissionState {
  const state: PermissionState = {};

  permissions.forEach((perm) => {
    state[perm.table_name] = {
      enabled: true,
      read: perm.can_read,
      write: perm.can_write,
      delete: perm.can_delete,
      fields: perm.allowed_fields || [],
    };
  });

  return state;
}

// Convert UI PermissionState to API format
export function stateToPermissions(
  state: PermissionState,
  userId: string
): TablePermission[] {
  const permissions: TablePermission[] = [];

  Object.entries(state).forEach(([tableName, perms]) => {
    if (perms.enabled) {
      permissions.push({
        id: 0, // Will be assigned by DB
        user_id: userId,
        table_name: tableName,
        can_read: perms.read,
        can_write: perms.write,
        can_delete: perms.delete,
        allowed_fields: perms.fields,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  });

  return permissions;
}
